import DtvBox from "@directv-digital/dds.ui.dtv-box";
import React, { useState } from "react";
import { keyframes } from "@emotion/react";
import DtvButton, { IDtvButtonProps } from "@directv-digital/dds.ui.dtv-button";
import DtvText from "@directv-digital/dds.ui.dtv-text";
import DtvAvatar from "@directv-digital/dds.ui.dtv-avatar";
import DtvGrid from "@directv-digital/dds.ui.dtv-grid";
import { colorPalette } from "@directv-digital/dds.ui.dtv-theme";
import CardMedia from "@mui/material/CardMedia";
import SkipNavigationAccessibility from "@directv-digital/dds.ui.skip-navigation-accessibility";
import DtvCountdown from "@directv-digital/dds.ui.dtv-countdown";
import CountdownTimerBlocks from "./countdown-timer-blocks"

export interface DtvFlashProps {
  variant?: string;
  mode?: string;
  barColors?: BarColor;
  headline?: any;
  heading?: any;
  disclaimer?: any;
  description?: any;
  legalText?: any;
  image?: Image;
  cta?: IDtvButtonProps;
  arrowButton?: any;
  heightDefaultHover?: HeightDefaultHover;
  analytics?: any;
  disableSkipNavigation?: boolean;
  open?: boolean;
  id?: string;
  countDownTimer?: any; // adds DtvCountdown component
  countdownTimerBlocks?: any; // adds CountdownTimerBlocks component
}

interface Image {
  imageUrl?: any;
  imageAlt?: any;
}

interface BarColor {
  backgroundColor?: any;
  opacityBarOne?: any;
  opacityBarTwo?: any;
  opacityBarThree?: any;
}

interface HeightDefaultHover {
  dtvFlashV1Mobile?: any;
  dtvFlashV1Desktop?: any;
  dtvFlashV2V3Mobile?: any;
  dtvFlashV2V3Desktop?: any;
}

enum variants {
  v1 = "DtvFlash_Plain",
  v2 = "DtvFlash_Image_NoCTA",
  v3 = "DtvFlash_Image_CTA",
}

// eslint-disable-next-line complexity
export function DtvFlash(props: DtvFlashProps) {
  try {
    const [isHovered, setIsHovered] = useState(false);
    const [showContent, setShowContent] = useState(props.open || false);
    const [animate, setAnimate] = useState(true);
    if (props?.disclaimer?.cta?.label) {
      props .disclaimer.cta.label = props?.disclaimer?.cta?.label?.replace(".", "")
    }

    const handleShowButton = () => {
      setShowContent(!showContent);
      setIsHovered(false);
      setAnimate(false);
    };

    const setHeight = (
      heightV1,
      isHoverededHeightV1,
      showContentHeightV1,
      heightV2V3,
      isHoverededHeightV2V3,
      showContentHeightV2V3
    ) => {
      if (showContent && props.variant === variants.v1) {
        return showContentHeightV1;
      } else if (showContent && props.variant !== variants.v1) {
        return showContentHeightV2V3;
      } else if (isHovered && props.variant === variants.v1) {
        return isHoverededHeightV1;
      } else if (isHovered && props.variant !== variants.v1) {
        return isHoverededHeightV2V3;
      } else if (!isHovered && !showContent && props.variant === variants.v1) {
        return heightV1;
      }
      return heightV2V3;
    };

    const handleHover = (mouseEnter: boolean) => {
      !showContent && setIsHovered(mouseEnter);
    }

    const mobileBarOneAnimationV1 = keyframes`
    0% {
      clip-path: polygon(50% 50px, 100% 31px, 100% 0%, 0% 0%, 0% 31px);
      height: 60px
    }
    100% {
      clip-path: polygon(49.86% 58px, 100% 36px, 100% 0%, 0% 0%, 0% 36px);
      height: 80px
    }
  `;
    const mobileBarTwoAnimationV1 = keyframes`
    0% {
      clip-path: polygon(100% 44.5px, 100% 0%, 0% 0%, 0% 60px);
      height: 60px;
    }
    100% {
      clip-path: polygon(100% 65px, 100% 0%, 0% 0%, 0% 80px);
      height: 80px;
    }
  `;
    const mobileBarThreeAnimationV1 = keyframes`
    0% {
      clipPath: polygon(100% 60px, 100% 0%, 0% 0%, 0% 44.5px);
      height: 60px;
    }
    100% {
      clip-path: polygon(100% 80px, 100% 0%, 0% 0%, 0% 65px);
      height: 80px;
    }
  `;
    const mobileBarOneAnimation = keyframes`
  0% {
    clip-path: polygon(50% 50px, 100% 31px, 100% 0%, 0% 0%, 0% 31px);
    height: 124px
  }
  100% {
    clip-path: polygon(49.86% 58px, 100% 36px, 100% 0%, 0% 0%, 0% 36px);
    height: 127px
  }
  `;
    const mobileBarTwoAnimation = keyframes`
  0% {
    clip-path: polygon(100% 44.5px, 100% 0%, 0% 0%, 0% 60px);
    height: 124px;
  }
  100% {
    clip-path: polygon(100% 65px, 100% 0%, 0% 0%, 0% 80px);
    height: 127px;
  }
  `;
    const mobileBarThreeAnimation = keyframes`
  0% {
    clipPath: polygon(100% 60px, 100% 0%, 0% 0%, 0% 44.5px);
    height: 124px;
  }
  100% {
    clip-path: polygon(100% 80px, 100% 0%, 0% 0%, 0% 65px);
    height: 127px;
  }
  `;
    const mobileImageAnimation = keyframes`
    0% {
      height: 88px;
      width:114px;
      top:36px;
    }
    100% {
      height: 104px;
      width:134px;
      top:36px;
    }
  `;
    const mobileArrowIconAnimation = keyframes`
  0% {
    height: 16px;
    width: 16px;
  }
  100% {
    height: 24px;
    width: 24px;
  }
  `;

    // eslint-disable-next-line camelcase
    const dtvCountDown_Data =
      props?.countDownTimer || null;
    const countdownTimerBlocksData =
      props?.countdownTimerBlocks && props?.countdownTimerBlocks?.[0] ?
        props.countdownTimerBlocks[0] :
        props?.countdownTimerBlocks ?
          props.countdownTimerBlocks :
          null;

    return (
      <>
        {!props?.disableSkipNavigation && <SkipNavigationAccessibility />}
        <DtvBox
          id={`${props?.id}-commonBanner`}
          sx={{
            paddingBottom: {
              xs: "20px",
              md: "0px"
            },
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            maxWidth: "2000px",
            width: "100%",
            mx: "auto"
          }}
        >
          <DtvBox
            sx={{
              width: "100%"
            }}
          >
            <DtvBox
              sx={{
                position: "relative",
                width: "100%",
                height: {
                  xs: setHeight(
                    props?.heightDefaultHover?.dtvFlashV1Mobile || "100%",
                    props?.heightDefaultHover?.dtvFlashV1Mobile || "100%",
                    "100%",
                    props?.heightDefaultHover?.dtvFlashV2V3Mobile || "100%",
                    props?.heightDefaultHover?.dtvFlashV2V3Mobile || "100%",
                    "100%"
                  ),
                  md: setHeight(
                    props?.heightDefaultHover?.dtvFlashV1Desktop || "100%",
                    props?.heightDefaultHover?.dtvFlashV1Desktop || "100%",
                    "100%",
                    props?.heightDefaultHover?.dtvFlashV2V3Desktop || "100%",
                    props?.heightDefaultHover?.dtvFlashV2V3Desktop || "100%",
                    "100%"
                  )
                },
                transition: "height 0.8s"
              }}
            >
              <DtvBox
                sx={{
                  position: "relative",
                  width: "100%",
                  display: "grid",
                  mb: {
                    xs: props?.disclaimer?.content ? "8px" : "0px",
                    md: "0px"
                  }
                }}
              >
                <DtvBox
                  sx={{
                    background: `${props?.barColors?.backgroundColor}`,
                    gridRowStart: 1,
                    gridColumnStart: 1,
                    height: {
                      xs: setHeight(
                        "60px",
                        "80px",
                        "162px",
                        "124px",
                        "127px",
                        "235px"
                      ),
                      md: setHeight(
                        "160px",
                        "160px",
                        "220px",
                        "168px",
                        "158px",
                        "224px"
                      )
                    },
                    width: "100%",
                    transition: "clip-path 0.8s, height 0.8s",
                    clipPath: {
                      xs: showContent ?
                        "polygon(50% 130.5px, 100% 81px, 100% 0%, 0% 0%, 0% 81px)" :
                        isHovered ?
                          "polygon(49.86% 58px, 100% 36px, 100% 0%, 0% 0%, 0% 36px)" :
                          "polygon(50% 50px, 100% 31px, 100% 0%, 0% 0%, 0% 31px)",
                      md: showContent ?
                        props.variant === variants.v1 ?
                          "polygon(50% 170px, 100% 106px, 100% 0%, 0% 0%, 0% 106px)" :
                          "polygon(74.61% 180px, 100% 85px, 100% 0%, 0% 0%, 0% 104px)" :
                        isHovered ?
                          props.variant === variants.v1 ?
                            "polygon(50% 140px, 100% 106px, 100% 0%, 0% 0%, 0% 106px)" :
                            "polygon(71.14% 145px, 100% 65px, 100% 0%, 0% 0%, 0% 65px)" :
                          props.variant === variants.v1 ?
                            "polygon(50% 138px, 100% 106px, 100% 0%, 0% 0%, 0% 106px)" :
                            "polygon(68.75% 144px, 100% 100px, 100% 0%, 0% 0%, 0% 106px)"
                    },
                    animationDuration: "0.8s",
                    animationName: {
                      xs:
                        !showContent && animate && props.variant === variants.v1 ?
                          `${mobileBarOneAnimationV1}` :
                          !showContent &&
                            animate &&
                            props.variant !== variants.v1 ?
                            `${mobileBarOneAnimation}` :
                            "none",
                      md: "none"
                    },
                    animationIterationCount: "2",
                    animationDirection: "alternate"
                  }}
                />

                <DtvBox
                  sx={{
                    gridRowStart: 1,
                    gridColumnStart: 1,
                    background: `${props?.barColors?.backgroundColor}`,
                    opacity: "0.262",
                    height: {
                      xs: setHeight(
                        "60px",
                        "80px",
                        "162px",
                        "124px",
                        "127px",
                        "235px"
                      ),
                      md: setHeight(
                        "160px",
                        "160px",
                        "220px",
                        "168px",
                        "158px",
                        "224px"
                      )
                    },
                    width: "100%",
                    transition: "clip-path 0.8s, height 0.8s",
                    clipPath: {
                      xs: showContent ?
                        "polygon(100% 120.5px, 100% 0%, 0% 0%, 0% 155.5px)" :
                        isHovered ?
                          "polygon(100% 65px, 100% 0%, 0% 0%, 0% 80px)" :
                          "polygon(100% 44.5px, 100% 0%, 0% 0%, 0% 60px)",
                      md: showContent ?
                        props.variant === variants.v1 ?
                          "polygon(100% 158px, 100% 0%, 0% 0%, 0% 206px)" :
                          "polygon(100% 158px, 100% 0%, 0% 0%, 0% 206px)" :
                        isHovered ?
                          props.variant === variants.v1 ?
                            "polygon(100% 88px, 100% 0%, 0% 0%, 0% 148px)" :
                            "polygon(100% 117px, 100% 0%, 0% 0%, 0% 152px)" :
                          props.variant === variants.v1 ?
                            "polygon(100% 168px, 100% 0%, 0% 0%, 0% 138px)" :
                            "polygon(100% 126px, 100% 0%, 0% 0%, 0% 168px)"
                    },
                    animationDuration: "0.8s",
                    animationName: {
                      xs:
                        !showContent && animate && props.variant === variants.v1 ?
                          `${mobileBarTwoAnimationV1}` :
                          !showContent &&
                            props.variant !== variants.v1 &&
                            animate ?
                            `${mobileBarTwoAnimation}` :
                            "none",
                      md: "none"
                    },
                    animationIterationCount: "2",
                    animationDirection: "alternate"
                  }}
                />
                <DtvBox
                  sx={{
                    gridRowStart: 1,
                    gridColumnStart: 1,
                    background: `${props?.barColors?.backgroundColor}`,
                    opacity: "0.262",
                    height: {
                      xs: setHeight(
                        "60px",
                        "80px",
                        "162px",
                        "124px",
                        "127px",
                        "235px"
                      ),
                      md: setHeight(
                        "160px",
                        "160px",
                        "220px",
                        "168px",
                        "158px",
                        "224px"
                      )
                    },
                    width: "100%",
                    transition: "clip-path 0.8s, height 0.8s",
                    clipPath: {
                      xs: showContent ?
                        "polygon(100% 155.5px, 100% 0%, 0% 0%, 0% 120.5px)" :
                        isHovered ?
                          "polygon(100% 80px, 100% 0%, 0% 0%, 0% 65px)" :
                          "polygon(100% 60px, 100% 0%, 0% 0%, 0% 44.5px)",
                      md: showContent ?
                        props.variant === variants.v1 ?
                          "polygon(100% 208px, 100% 0%, 0% 0%, 0% 140px)" :
                          "polygon(100% 268px, 100% 0%, 0% 0%, 0% 140px)" :
                        isHovered ?
                          props.variant === variants.v1 ?
                            "polygon(100% 138px, 100% 0%, 0% 0%, 0% 98px)" :
                            "polygon(100% 156.5px, 100% 0%, 0% 0%, 0% 86px)" :
                          props.variant === variants.v1 ?
                            "polygon(100% 126px, 100% 0%, 0% 0%, 0% 168px)" :
                            "polygon(100% 166px, 100% 0%, 0% 0%, 0% 136px)"
                    },
                    animationDuration: "0.8s",
                    animationName: {
                      xs:
                        !showContent && animate && props.variant === variants.v1 ?
                          `${mobileBarThreeAnimationV1}` :
                          !showContent &&
                            props.variant !== variants.v1 &&
                            animate ?
                            `${mobileBarThreeAnimation}` :
                            "none",
                      md: "none"
                    },
                    animationIterationCount: "2",
                    animationDirection: "alternate"
                  }}
                />
                {(props.variant === variants.v2 ||
                  props.variant === variants.v3) &&
                  props?.image?.imageUrl && (
                    <DtvBox
                      sx={{
                        display: "flex",
                        justifyContent: {
 xs: "center",
md: "normal"
},
                        mt: {
                              xs: "10px",
                              md: "0px"
                            }
                      }}
                    >
                      <DtvBox
                        sx={{
                          position: "absolute",
                          transformOrigin: {
 xs: "center",
md: "top left"
},
                          mt: {
                            xs: props?.disclaimer?.content ? "44px" : "36px",
                            md: "8px"
                          },
                          right: {
                            md: 'calc(75px + 12.5%)', // right === mr + ((100% - mr - ml) * (3/12) / 2) === 100px + 12.5% - 25px
                            lg: 'calc(105px + 12.5%)' // Must be pos:abs to get transitions so mimicking centering
                          },
                          transform: {
                            md: "translate(50%, 0%)"
                          },
                          top: {
                            xs: showContent ?
                              "92px" :
                              isHovered ?
                                "26px" :
                                "36px",
                            md: showContent ?
                              "28px" :
                              isHovered ?
                                "28px" :
                                "40px"
                          },
                          height: {
                            xs: showContent ?
                              "136PX" :
                              isHovered ?
                                "104px" :
                                "88px",
                            md: showContent ?
                              "240PX" :
                              isHovered ?
                                "160px" :
                                "136px"
                          },
                          width: {
                            xs: showContent ?
                              "176PX" :
                              isHovered ?
                                "134px" :
                                "114px",
                            md: showContent ?
                              "312PX" :
                              isHovered ?
                                "208px" :
                                "176px"
                          },
                          transition: "height 0.8s, width 0.8s, top 0.8s, right 0.8s",
                          animationDuration: "0.8s",
                          animationName: {
                            xs:
                              !showContent && animate ?
                                `${mobileImageAnimation}` :
                                "none",
                            md: "none"
                          },
                          animationIterationCount: "2",
                          animationDirection: "alternate"
                        }}
                      >
                        <CardMedia
                          alt={`${props?.image?.imageAlt}`}
                          component="img"
                          id={`${props?.id}-image`}
                          src={`${props?.image?.imageUrl}`}
                          sx={{
                            height: "100%"
                          }}
                        />
                      </DtvBox>
                    </DtvBox>
                  )}
                {props?.headline?.content && (
                  <DtvGrid
                    container
                    sx={{
                      display: "flex",
                      paddingTop: {
                        xs: showContent ? "24px" : "12px",
                        md: showContent ? "102px" : isHovered ? "44px" : "64px"
                      },
                      transition: isHovered ? "padding-top 0.8s" : "padding-top 0.8s",
                      px: {
                        md: "100px",
                        lg: "140px"
                      },
                      position: "absolute",
                      zIndex: "200"
                    }}
                  >
                    {(props?.variant === variants.v2 ||
                      props?.variant === variants.v3) && (
                        <DtvGrid
                          item
                          xs={12}
                          sx={{
                            display: "flex",
                            flexDirection: "column",
                            alignItems: {
 xs: "center",
md: "flex-start"
},
                            justifyContent: {
 xs: "center",
md: "left"
},
                            transformOrigin: {
 xs: "center",
md: "top"
},
                            transition: showContent ?
                              "max-width 0.8s" :
                              "max-width 0.8s"
                          }}
                        ></DtvGrid>
                      )}

                    <DtvGrid
                      item
                      xs={12}
                      md={
                        props?.variant === variants.v2 ||
                          props?.variant === variants.v3 ?
                          9 :
                          12
                      }
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: {
                          xs: "center",
                          md:
                            props?.variant === variants.v2 ||
                              props?.variant === variants.v3 ?
                              "flex-start" :
                              "center"
                        },
                        justifyContent: {
                          xs: "center",
                          md:
                            props?.variant === variants.v2 ||
                              props?.variant === variants.v3 ?
                              "left" :
                              "center"
                        }
                      }}
                    >
                      <DtvText
                        aria-label={props?.headline?.content}
                        component={props?.headline?.component || "h3"}
                        id={`${props?.id}-headline`}
                        variant={"h3"}
                        sx={{
                          textAlign: {
 xs: "center",
md: "left"
},
                          typography: {
                            xs: showContent ? "h2" : "subhead2medium",
                            md: "h3"
                          },
                          color:
                            props?.mode === "dark" ?
                              colorPalette.neutral_white.main :
                              colorPalette.jet.main,
                          mb: {
                            xs: "0px",
                            md: "8px"
                          },
                          transition: "font-size 0.6s"
                        }}
                        {...props?.headline}
                      />
                      {props?.disclaimer?.content && (
                        <>
                          <DtvText
                            id={`${props?.id}-disclaimer`}
                            inlineLinkStyle={{
                              verticalAlign: "bottom",
                              paddingLeft: "4px",
                              display: 'inline',
                              textAlign: {
                                xs: "center",
                                md: "left"
                              },
                              mt: {
                                md: "8px",
                                xs: "4px"
                              },
                              lineHeight: "1.4em",
                              fontSize: {
                                xs: "14px",
                                md: "14px"
                              },
                              fontFamily: {
                                xs: "roboto-regular",
                                md: "roboto-medium"
                              }
                            }}
                            sx={{
                              textAlign: {
 xs: "center",
md: "left"
},
                              fontSize: {
                                xs: "14px",
                                md: "14px"
                              },
                              mb: {
                                xs: "0px",
                                md: "8px"
                              }
                            }}
                            {...props?.disclaimer}
                          />
                        </>
                      )}
                    </DtvGrid>
                  </DtvGrid>
                )}
              </DtvBox>
              <DtvBox
                sx={{
                  position: "relative",
                  width: "100%",
                  display: "flex",
                  flexDirection: "column-reverse",
                  mt:
                  {
                    xs: "26px",
                    md: "0px"
                  },
                  px: {
                    xs: "20px",
                    md: "100px",
                    lg: "140px"
                  },
                  ...(props?.countdownTimerBlocks && props?.headline && showContent) && {
                    mt: { md: "-32px" } // 224px (clip-path height) - 182px (heading height) * -1
                  }
                }}
              >
                <DtvButton
                  analytics={{ linkPosition: "headband" }}
                  aria-controls={`flash-collapse-content-${  props.id}`}
                  aria-expanded={showContent}
                  component="a"
                  id={`flash-collapse-anchor-${  props.id}`}
                  label={showContent ? 'Close' : 'Open'}
                  promo={props?.cta?.promo}
                  variant="uxtext"
                  // WAT-12478 — GTM names the click from aria-label, so open and close need
                  // distinct names (the CMS arrowButton.label still overrides the close name).
                  aria-label={
                    showContent ?
                      props?.arrowButton?.label || "close flash content" :
                      "open flash content"
                  }
                  endIcon={
                    <DtvAvatar
                      src="https://www.directv.com/dtvassets/global/icons/fusion/arrow_jet_down.svg"
                      alt={
                        showContent ?
                          "flash-content-expanded" :
                          "flash-content-collapsed"
                      }
                      sx={{
                        height: showContent ? "24px" : isHovered ? "24px" : "16px",
                        width: showContent ? "24px" : isHovered ? "24px" : "16px",
                        transform: showContent ? "rotate(180deg)" : "rotate(360deg)",
                        transition: "transform 0.5s, height 0.8s, width 0.8s",
                        animationDuration: "0.8s",
                        animationName: {
                          xs:
                            !showContent && animate ?
                              `${mobileArrowIconAnimation}` :
                              "none",
                          md: "none"
                        },
                        animationIterationCount: "2",
                        animationDirection: "alternate"
                      }}
                    />
                  }
                  sx={{
                    textDecoration: "none",
                    typography: "body2",
                    position: "relative",
                    width: "max-content",
                    flexDirection: "row",
                    fontWeight: "500",
                    justifyContent: "center",
                    borderRadius: "0px",
                    "&:hover": {
                      textDecoration: "none",
                      backgroundColor: "transparent"
                    },
                    "&:focus": {
                      textDecoration: "none",
                      backgroundColor: "transparent"
                    },
                    "& .MuiButton-endIcon,& .DtvButton-endIcon": {
                      mr: "0px",
                      ml: "8px"
                    },
                    margin: "auto",
                    mb: {
                      xs: showContent ? "8px" : "12px",
                      md: "12px"
                    },
                    mt: {
                      xs: "0px",
                      md: showContent ? "60px" : "12px"
                    }
                  }}
                  onClick={handleShowButton}
                  onMouseEnter={() => handleHover(true)}
                  onMouseLeave={() => handleHover(false)}
                />
                <DtvBox
                  aria-labelledby={`flash-collapse-anchor-${  props.id}`}
                  id={`flash-collapse-content-${  props.id}`}
                  role="group"
                  sx={{
                    display: "grid",
                    gridTemplateRows: showContent ? "2fr" : "0fr",
                    transition: showContent ?
                      "grid-template-rows 0.8s" :
                      "grid-template-rows 0.8s"
                  }}
                >
                  <DtvGrid
                    container
                    sx={{
                      ...(!showContent ? { visibility: "hidden" } : {}),
                      display: "flex",
                      flexDirection: { md: "row" },
                      position: "relative",
                      transition: "margin-bottom 0.8s",
                      overflow: "hidden"
                    }}
                  >
                    <DtvGrid
                      item
                      xs={12}
                      md={
                        props?.variant === variants.v2 ||
                          props?.variant === variants.v3 ?
                          9 :
                          12
                      }
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: {
                          xs: "center",
                          md:
                            props?.variant === variants.v2 ||
                              props?.variant === variants.v3 ?
                              "flex-start" :
                              "center"
                        },
                        justifyContent: {
                          xs: "center",
                          md:
                            props?.variant === variants.v2 ||
                              props?.variant === variants.v3 ?
                              "left" :
                              "center"
                        },
                        paddingRight: {
                          md:
                            props?.variant === variants.v2 ||
                              props?.variant === variants.v3 ?
                              "60px" :
                              "0px"
                        }
                      }}
                    >
                      {(props?.countdownTimerBlocks && !props?.countDownTimer) &&
                        <>
                        <DtvBox>
                          <CountdownTimerBlocks {...countdownTimerBlocksData} />
                        </DtvBox>
                        </>
                      }
                      {props?.heading?.content && (
                        <>
                        <DtvText
                          aria-label={props?.heading?.content}
                          component={props?.heading?.component || "div"}
                          id={`${props?.id}-heading`}
                          variant="subhead1medium"
                          sx={{
                            textAlign: {
                              xs: "center",
                              md:
                                props?.variant === variants.v2 ||
                                  props?.variant === variants.v3 ?
                                  "left" :
                                  "center"
                            },
                            mb: {
                              xs: "24px",
                              md: "8px"
                            },
                            fontWeight: 500
                          }}
                          {...props?.heading}
                        />
                        </>
                      )}
                      {(props?.countDownTimer && !props?.countdownTimerBlocks) &&
                        <>
                        <DtvBox sx={{ paddingBottom: '15px' }}>
                          {/* eslint-disable-next-line camelcase */}
                          <DtvCountdown { ...dtvCountDown_Data } />
                        </DtvBox>
                        </>
                      }
                      {props?.description?.content && (
                        <>
                        <DtvText
                          aria-label={props?.description?.content}
                          component={props?.description?.component || "div"}
                          id={`${props?.id}-description`}
                          variant="body1"
                          sx={{
                            textAlign: {
                              xs: "center",
                              md:
                                props?.variant === variants.v2 ||
                                  props?.variant === variants.v3 ?
                                  "left" :
                                  "center"
                            },
                            mb: {
                              xs: "16px",
                              md: "8px"
                            }
                          }}
                          {...props?.description}
                        />
                        </>
                      )}

                      {props?.legalText && (
                        <DtvText
                          cta={props?.legalText}
                          id={`${props?.id}-legalText`}
                          sx={{
                            mb: {
                              xs: "16px",
                              md: "8px"
                            },
                            "& .MuiButton-root": {
                              ml: "0px"
                            },
                            px: "0px",
                            fontSize: {
                              xs: "14px",
                              md: "18px"
                            },
                            fontWeight: "bold",
                            textDecoration: "underline"
                          }}
                        />
                      )}
                    </DtvGrid>
                    {props?.variant === variants.v3 && props?.cta ? (
                      <DtvGrid
                        item
                        md={3}
                        xs={12}
                        sx={{
                          display: "flex",
                          flexDirection: "column",
                          alignItems: "center",
                          justifyContent: "space-around",
                          marginTop: { md: "42px" }
                        }}
                      >
                        <DtvButton
                          ariaLabel={props?.cta?.label}
                          id={`${props?.id}-cta`}
                          label={props?.cta?.label}
                          sx={{
                            mb: {
                              xs: "16px",
                              md: "0px"
                            },
                            width: {
                              md: "85%"
                            },
                            maxWidth: "335px"
                          }}
                          {...props.cta}
                        />
                      </DtvGrid>
                    ) : (
                      props?.variant === variants.v2 && (
                        <DtvGrid item md={5} xs={12}></DtvGrid>
                      )
                    )}
                  </DtvGrid>
                </DtvBox>
              </DtvBox>
            </DtvBox>
          </DtvBox>
        </DtvBox>
      </>
    );
  } catch(e: any) {
    console.error(e)
    return <></>
  }
}

